import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartDataPoint, ChartConfig } from '../types';
import { getYearMonth, convertToPercentage, convertToMinutes, fillMissingDates } from '../utils';
import { CustomXAxisTick } from './CustomXAxisTick';
import { CustomTooltip } from './CustomTooltip';

type ChartComponentProps = {
  data: ChartDataPoint[];
  config: ChartConfig;
  startDate: string;
  endDate: string;
};

export const ChartComponent: React.FC<ChartComponentProps> = ({ data, config, startDate, endDate }) => {
  console.log('ChartComponent レンダリング開始:', { data, config, startDate, endDate });
  
  // データが存在しない場合の安全な処理
  if (!data || !Array.isArray(data)) {
    console.warn('無効なデータが渡されました:', data);
    return (
      <div className="chart-container">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {config.title}
        </h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          データが利用できません
        </div>
      </div>
    );
  }

  // 日付範囲を補完してデータがない日も表示
  const filledData = fillMissingDates(data, startDate, endDate);
  console.log('日付補完後のデータ:', filledData);
  
  // データタイプに応じて変換
  const processedData = filledData.map(point => {
    try {
      return {
        ...point,
        value: point.value !== null && point.value !== undefined
          ? (config.dataKey.includes('rate') || config.dataKey === 'sleep_efficiency')
            ? convertToPercentage(point.value)
            : (config.dataKey.includes('time') && config.unit === '分')
              ? convertToMinutes(point.value)
              : point.value
          : null,
      };
    } catch (error) {
      console.error('データ変換エラー:', error, point);
      return { ...point, value: null };
    }
  });
  
  console.log('処理済みデータ:', processedData);

  const renderCustomXAxisTick = (props: any) => {
    const dataPoint = processedData.find(d => d.date === props.payload.value);
    const isWeekend = dataPoint?.isWeekend || false;
    return <CustomXAxisTick {...props} isWeekend={isWeekend} />;
  };

  const yearMonth = getYearMonth(startDate, endDate);

  return (
    <div className="chart-container">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {yearMonth} - {config.title}
      </h3>
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer>
          <LineChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="date"
              tick={renderCustomXAxisTick}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              interval={0}
            />
            <YAxis 
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              tick={{ fill: '#374151', fontSize: 12 }}
            />
            <Tooltip content={(props) => <CustomTooltip {...props} config={config} />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={config.color}
              strokeWidth={2}
              dot={{ fill: config.color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: config.color }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};